/**
 * ErrorBoundary - Catches runtime errors in ClassroomBoard
 * Prevents white screens, shows friendly error UI with reload option
 */
import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            copied: false
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        console.error('ClassroomBoard Error:', error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleCopyError = () => {
        const errorDetails = `
Error: ${this.state.error?.message || 'Unknown error'}
Stack: ${this.state.error?.stack || 'No stack trace'}
Component Stack: ${this.state.errorInfo?.componentStack || 'No component stack'}
Time: ${new Date().toISOString()}
URL: ${window.location.href}
        `.trim();

        navigator.clipboard.writeText(errorDetails).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-6">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
                        {/* Icon */}
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl font-bold text-slate-800 mb-2">
                            Something went wrong
                        </h1>
                        <p className="text-slate-600 mb-6">
                            The Classroom Board encountered an error. Your data is safe.
                        </p>

                        {/* Error message (collapsed by default) */}
                        <details className="text-left mb-6 bg-slate-50 rounded-xl p-4">
                            <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-800">
                                View error details
                            </summary>
                            <pre className="mt-3 text-xs text-red-600 overflow-auto max-h-32 bg-white p-3 rounded-lg border">
                                {this.state.error?.message || 'Unknown error'}
                            </pre>
                        </details>

                        {/* Actions */}
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={this.handleReload}
                                className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
                            >
                                <RefreshCw size={18} />
                                Reload Page
                            </button>
                            <button
                                onClick={this.handleCopyError}
                                className="flex items-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                            >
                                {this.state.copied ? (
                                    <>
                                        <Check size={18} className="text-emerald-500" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy size={18} />
                                        Copy Error
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Help text */}
                        <p className="text-xs text-slate-400 mt-6">
                            If this keeps happening, share the error details with your administrator.
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
